import type { ChatAnnotation } from "~/pages/appointments/hooks/useAgentChat";

/** Marks a selectable message row in the list; its value is the saved
 *  message ID, the reply's render key until that ID arrives, or empty. */
export const MESSAGE_ROW_ATTRIBUTE = "data-indy-message-row";

/** A sent message's annotation chip: its label is not message text, so it is
 *  neither staged from nor searched when jumping back. */
export const SENT_ANNOTATIONS_SELECTOR = ".indy-annotations__sent";

function inSentAnnotations(node: Node): boolean {
  const element = node instanceof Element ? node : node.parentElement;
  return element?.closest(SENT_ANNOTATIONS_SELECTOR) != null;
}

function enclosingRow(node: Node, root: Element): Element | null {
  const element = node instanceof Element ? node : node.parentElement;
  const row = element?.closest(`[${MESSAGE_ROW_ATTRIBUTE}]`) ?? null;
  return row && root.contains(row) ? row : null;
}

/**
 * The annotation a selection would stage: its text and the message it sits
 * in. Null unless the selection is non-empty and both ends land in the same
 * message row under `root` — a drag across two messages, or one starting
 * outside the list, stages nothing.
 */
export function annotationFromSelection(
  selection: Selection | null,
  root: Element,
): ChatAnnotation | null {
  if (!selection || selection.isCollapsed || selection.rangeCount === 0)
    return null;
  const text = selection.toString().trim();
  if (!text) return null;
  const range = selection.getRangeAt(0);
  if (inSentAnnotations(range.startContainer)) return null;
  if (inSentAnnotations(range.endContainer)) return null;
  const row = enclosingRow(range.startContainer, root);
  if (!row || row !== enclosingRow(range.endContainer, root)) return null;
  const messageId = row.getAttribute(MESSAGE_ROW_ATTRIBUTE);
  return messageId ? { text, sourceMessageId: messageId } : { text };
}

/** The row rendering `messageId`, if it is in the list. */
export function findMessageRow(
  root: Element,
  messageId: string,
): HTMLElement | null {
  for (const row of root.querySelectorAll<HTMLElement>(
    `[${MESSAGE_ROW_ATTRIBUTE}]`,
  )) {
    if (row.getAttribute(MESSAGE_ROW_ATTRIBUTE) === messageId) return row;
  }
  return null;
}

/**
 * The range inside `root` whose text is `text`, ignoring whitespace: a
 * selection's `toString()` adds line breaks between blocks that the DOM's
 * text nodes don't hold. Text under `skipSelector` (a message's own
 * annotation list) is not searched. First match wins.
 */
export function findTextRange(
  root: Element,
  text: string,
  skipSelector: string,
): Range | null {
  const needle = text.replace(/\s+/g, "");
  if (!needle) return null;
  // One entry per non-whitespace character: where it sits in the DOM.
  const positions: { node: Text; offset: number }[] = [];
  let haystack = "";
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!(node instanceof Text) || node.parentElement?.closest(skipSelector))
      continue;
    const value = node.data;
    for (let offset = 0; offset < value.length; offset++) {
      if (/\s/.test(value[offset])) continue;
      positions.push({ node, offset });
      haystack += value[offset];
    }
  }
  const start = haystack.indexOf(needle);
  if (start < 0) return null;
  const first = positions[start];
  const last = positions[start + needle.length - 1];
  const range = document.createRange();
  range.setStart(first.node, first.offset);
  range.setEnd(last.node, last.offset + 1);
  return range;
}

export function annotationCountLabel(count: number): string {
  return count === 1 ? "1 annotation" : `${count} annotations`;
}
